import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "../../components/account/Navbar";
import { getUserClinicAppointments, clinicAPI, petAPI } from "../../services/api";
import MedicalRecordView from "../../components/petProfiles/MedicalRecordView";
import SOAPDetailsContent from "../../components/reception/SOAPDetailsContent";
import flatpickr from "flatpickr";
import { showSuccessToast, showErrorToast, showActionToast } from "../../utils/notificationUtils";
import "flatpickr/dist/flatpickr.min.css";
import { useFlatpickrUserType } from "../../hooks/useFlatpickrUserType";
import { useAuth } from "../../hooks/useAuth";
import "../../styles/guardiandash.css";
import RatingModal from "../../components/modals/RatingModal";

const GuardianDash = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { getUserType } = useAuth();
  const userType = getUserType();
  const isRTL = i18n?.dir?.() === "rtl";
  const locale = i18n?.language || "en";
  // Mark body/html as guardian dashboard page so we can control scroll behavior
  useEffect(() => {
    document.body.classList.add("guardian-dash-page");
    document.documentElement.classList.add("guardian-dash-page");

    return () => {
      document.body.classList.remove("guardian-dash-page");
      document.documentElement.classList.remove("guardian-dash-page");
    };
  }, []);

  // Apply user type color to Flatpickr calendars globally
  useFlatpickrUserType();

  // Get the correct urgent case submit URL based on user type
  const getUrgentCaseSubmitUrl = () => {
    return userType === 'SERVICE_PROVIDER'
      ? '/medical/dashboard/petshopowner/urgent-case-submit'
      : '/medical/dashboard/petguardian/urgent-case-submit';
  };

  // Get the correct appointment request URL based on user type
  const getAppointmentRequestUrl = () => {
    return userType === 'SERVICE_PROVIDER'
      ? '/medical/dashboard/petshopowner/appointment-request'
      : '/medical/dashboard/petguardian/appointment-request';
  };

  const { tab: routeTab } = useParams();

  // Check URL parameter for initial tab
  const getInitialTab = () => {
    const params = new URLSearchParams(location.search);
    const tabParam = routeTab || params.get('tab');
    const defaultTab = userType === 'FIRM' ? 'overview' : 'sos';
    return (tabParam === 'appointments' || tabParam === 'overview' || (tabParam === 'sos' && userType !== 'FIRM'))
      ? tabParam
      : defaultTab; // default tab based on user type
  };

  const [activeTab, setActiveTab] = useState(getInitialTab());

  // State for medical records and appointments
  const [appointments, setAppointments] = useState([]);
  const [pets, setPets] = useState([]);
  const [selectedPet, setSelectedPet] = useState(null);
  const [selectedRecordType, setSelectedRecordType] = useState('vaccinations');
  const [selectedPetForAppointments, setSelectedPetForAppointments] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [appointmentToCancel, setAppoitmentToCancel] = useState(null);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showReschedulePreview, setShowReschedulePreview] = useState(false);
  const [appointmentToReschedule, setAppointmentToReschedule] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [loadingTimeSlots, setLoadingTimeSlots] = useState(false);
  // Clinic rating modal state (for completed appointments)
  const [ratingModalAppointment, setRatingModalAppointment] = useState(null);
  const [ratingValue, setRatingValue] = useState(0);
  const [ratingComment, setRatingComment] = useState("");
  const [isSubmittingRating, setIsSubmittingRating] = useState(false);

  // Past visits state
  const [pastVisits, setPastVisits] = useState([]);
  const [loadingPastVisits, setLoadingPastVisits] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState(null);

  // Refs for Flatpickr and scroll container
  const reschedulePickerRef = useRef(null);
  const rescheduleFlatpickrInstance = useRef(null);
  const guardianScrollRef = useRef(null);
  const highlightedAppointmentRef = useRef(null);

  const resolvePetName = React.useCallback(
    (pet, fallbackKey = "guardianDash.pet.fallback.name") => {
      if (!pet) {
        return t(fallbackKey);
      }

      const { name } = pet;
      if (typeof name === "string" && name.trim().length > 0) {
        return name;
      }

      return (
        name?.en ||
        name?.ar ||
        t(fallbackKey)
      );
    },
    [t]
  );

  // Helper function to resolve pet profile image
  const resolvePetImage = (pet) => {
    if (!pet) return null;

    // Handle profile_image_path which might be a string or an object { url, path, ... }
    const profilePath =
      typeof pet.profile_image_path === "string"
        ? pet.profile_image_path
        : pet.profile_image_path?.url || pet.profile_image_path?.path;

    const candidates = [
      pet.primaryImageUrl,
      pet.primary_image_url,
      profilePath,
      pet.profile_image_url,
      pet.profileImageUrl,
      pet.image,
      pet.avatar,
      pet.photo,
    ];

    const chosen = candidates.find(
      (u) => typeof u === "string" && u.trim() !== "" && u !== '/placeholder-pet.png'
    );
    return chosen || null;
  };

  // Fetch user's pets
  useEffect(() => {
    const fetchPets = async () => {
      try {
        const response = await petAPI.getMyPets();
        const rawPetsData = response.data?.data || response.data || [];

        // Ensure each pet has proper string properties to prevent object rendering errors
        const extractStringValue = (value, fallback) => {
          if (typeof value === 'string') return value;
          if (typeof value === 'object' && value !== null) {
            // Handle multilingual objects (e.g., {en: "Buddy", ar: "صديق"})
            return value.en || value.ar || value.name || Object.values(value)[0] || fallback;
          }
          return value ? String(value) : fallback;
        };

        const petsData = Array.isArray(rawPetsData) ? rawPetsData.map(pet => ({
          ...pet,
          name: extractStringValue(pet.name, t("guardianDash.pet.fallback.name")),
          species: extractStringValue(pet.species, t("guardianDash.pet.fallback.species")),
          breed: extractStringValue(pet.breed, t("guardianDash.pet.fallback.breed")),
          age: extractStringValue(pet.age, t("guardianDash.pet.fallback.age"))
        })) : [];

        setPets(petsData);
        if (petsData.length > 0) {
          setSelectedPet(petsData[0]); // Select first pet by default
        }
      } catch (error) {
        console.error('Error fetching pets:', error);
      }
    };
    fetchPets();
  }, [t]);


  // Handle URL parameter changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = routeTab || params.get('tab');
    if (tabParam === 'appointments' || tabParam === 'overview' || tabParam === 'sos') {
      setActiveTab(tabParam);
    }
  }, [location.search, routeTab]);

  // Handle manual tab changes to keep URL in sync
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    
    // Update the URL to match the tab for direct linking
    // Ensure we keep existing search params (except 'tab') when replacing URL
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('tab');
    const searchString = searchParams.toString();
    const queryStr = searchString ? `?${searchString}` : '';
    
    // Determine base path
    const basePath = userType === 'SERVICE_PROVIDER' 
      ? '/medical/dashboard/petshopowner' 
      : '/medical/dashboard/petguardian';
      
    navigate(`${basePath}/${newTab}${queryStr}`, { replace: true });
  };

  // Fetch user's appointments - make it a reusable function
  const fetchAppointments = useCallback(async () => {
    try {
      setLoading(true);
      // Try to get all appointments including upcoming status
      // Backend might be filtering out "upcoming" by default
      const response = await getUserClinicAppointments({
        // Include all statuses - don't filter by status
        // If backend supports status parameter, we want to explicitly include "upcoming"
        include_all_statuses: true,
        // Alternative: try passing status as array or comma-separated
        // status: ['upcoming', 'active', 'pending', 'confirmed', 'new', 'completed']
      });

      // Extract appointments from the nested response structure
      const appointmentsData = response.data?.data?.appointments || [];

      // If no appointments returned, try alternative response structures
      const alternativeData = response.data?.appointments || response.data?.data || response.data || [];
      const allAppointments = appointmentsData.length > 0 ? appointmentsData : (Array.isArray(alternativeData) ? alternativeData : []);



      if (allAppointments.length === 0) {
        setAppointments([]);
        setError(null);
        setLoading(false);
        return;
      }

      // Format and sort the appointment data for the dashboard (Earliest to Furthest)
      const formattedAppointments = [...allAppointments].sort((a, b) => {
        return new Date(a.appointment_at) - new Date(b.appointment_at);
      });


      const finalAppointments = formattedAppointments.map(appointment => {
        const appointmentDate = new Date(appointment.appointment_at);
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison

        // Check if appointment is converted to outpatient or inpatient
        const hasOutpatientVisitId = typeof appointment.outpatient_visit_id === 'number' && appointment.outpatient_visit_id > 0;
        const hasInpatientVisitId = typeof appointment.inpatient_visit_id === 'number' && appointment.inpatient_visit_id > 0;
        const isConverted =
          appointment.status?.toLowerCase() === 'converted' ||
          appointment.converted_to_visit === true ||
          hasOutpatientVisitId ||
          hasInpatientVisitId;

        // Determine status based on conversion and API status
        // Backend now computes status based on appointment time, so we trust it completely
        // Backend returns: status (computed), computed_status (explicit), explicit_status (if set by vet)
        const computedStatus = appointment.computed_status || appointment.status;
        const apiStatus = computedStatus?.toLowerCase();
        let status;
        let visitType = null; // 'outpatient' or 'inpatient'

        // If converted, show as outpatient/inpatient visit
        // Only mark as completed if API explicitly says so (doctor closed the visit)
        if (isConverted) {
          // If doctor closed the visit, API will return 'completed'
          if (apiStatus === 'completed') {
            status = 'completed';
          } else if (hasInpatientVisitId) {
            status = 'inpatient_visit';
            visitType = 'inpatient';
          } else if (hasOutpatientVisitId) {
            status = 'outpatient_visit';
            visitType = 'outpatient';
          } else {
            status = 'converted';
          }
        }
        // For regular appointments, trust backend computed status completely
        // Backend calculates status based on appointment time vs current time:
        // - Future appointments → 'upcoming'
        // - Happening now (within 2 hours) → 'active'
        // - Past appointments (> 2 hours) → 'no_show' (unless explicitly completed)
        else {
          // Use the computed status directly from backend
          // Backend handles all time-based calculations
          status = apiStatus || 'upcoming';
        }

        return {
          id: appointment.id,
          clinicId: appointment.clinic_id, // Add clinic ID for working hours API
          vetId: appointment.vet_id, // Add vet ID for reschedule API
          petOwnerId: appointment.pet_owner_id, // Add Pet Guardian ID for reschedule API
          appointmentTypeId: appointment.appointment_type_id, // Add appointment type ID for reschedule API
          petId: appointment.pet?.id, // Add petId for filtering
          pet: appointment.pet, // Store full pet object for image resolution
          petName: appointment.pet?.name || t("guardianDash.pet.fallback.name"),
          date: appointment.appointment_at ? appointment.appointment_at.split('T')[0].split(' ')[0] : '',
          time: (() => {
            const str = appointment.appointment_at?.replace(' ', 'T') || '';
            if (!str) return '';
            if (!str.includes('T')) return new Date(`1970-01-01T${str}:00`).toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit', hour12: true });
            const localStr = str.endsWith('Z') ? str.slice(0, -1) : str;
            return new Date(localStr).toLocaleTimeString(locale, {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            });
          })(),
          vetName: `${appointment.vet?.first_name || ''} ${appointment.vet?.last_name || ''}`.trim() || t("guardianDash.appointments.fallbacks.vet"),
          clinicName: appointment.clinic?.name_en || appointment.clinic?.name_ar || t("guardianDash.appointments.fallbacks.clinic"),
          status: status,
          type: appointment.appointment_type?.title || t("guardianDash.appointments.fallbacks.type"),
          isConverted: isConverted, // Store conversion status
          visitType: visitType, // 'outpatient' or 'inpatient'
          outpatientVisitId: appointment.outpatient_visit_id,
          inpatientVisitId: appointment.inpatient_visit_id,
          appointmentDateTime: new Date(appointment.appointment_at), // Store full datetime for time comparison
          // Optional rating flags from backend (any of these can be used)
          // Only set to true if explicitly true from backend (defensive: backend may not have table yet)
          canRateClinic: Boolean(
            appointment.can_rate_clinic === true ||
            appointment.canRateClinic === true ||
            appointment.has_pending_clinic_rating === true
          )
        };
      });


      setAppointments(finalAppointments);
      setError(null);
    } catch (error) {
      console.error('❌ Error fetching appointments:', error);
      console.error('❌ Error details:', error.response?.data || error.message);
      console.error('❌ Full error response:', error.response);

      // Show user-friendly error message
      const errorMessage = error.response?.data?.message || error.message || t("guardianDash.appointments.errors.load");
      setError(errorMessage);
      setAppointments([]);

      // If it's a database/table error, show a more helpful message
      if (errorMessage.includes('clinic_ratings') || errorMessage.includes('Table') || errorMessage.includes('doesn\'t exist')) {
        console.warn('Backend clinic_ratings table may not exist yet. Rating features will be disabled until backend is updated.');
        // Don't show this to users - just log it. The appointments will still fail to load,
        // but at least we've logged the issue for debugging.
      }
    } finally {
      setLoading(false);
    }
  }, [t, locale]);

  // Fetch appointments on component mount
  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  // Refresh appointments when appointments tab is opened (including when navigating from notification)
  useEffect(() => {
    if (activeTab === 'appointments') {
      // Small delay to ensure component is fully mounted and URL params are processed
      const timer = setTimeout(() => {
        fetchAppointments();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [activeTab, location.search, fetchAppointments]);

  // Highlight and scroll to appointment when coming from notification
  useEffect(() => {
    if (activeTab !== 'appointments' || loading || appointments.length === 0) {
      return;
    }

    const highlightAppointmentId = sessionStorage.getItem('highlightAppointmentId');
    if (!highlightAppointmentId) {
      return;
    }

    // Clear the sessionStorage after a delay to allow scrolling
    setTimeout(() => {
      sessionStorage.removeItem('highlightAppointmentId');
    }, 3000);

    // Wait for appointments to render, then scroll to highlighted appointment
    setTimeout(() => {
      const appointmentElement = document.querySelector(
        `[data-appointment-id="${highlightAppointmentId}"]`
      );

      if (appointmentElement) {
        // Add highlight class
        appointmentElement.classList.add('appointment-highlighted');

        // Scroll to the appointment
        appointmentElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });

        // Remove highlight class after animation
        setTimeout(() => {
          appointmentElement.classList.remove('appointment-highlighted');
        }, 3000);
      }
    }, 100);
  }, [activeTab, loading, appointments]);

  // Fetch past visits for selected pet
  const fetchPastVisits = React.useCallback(async () => {
    if (!selectedPet?.id) return;
    setLoadingPastVisits(true);
    try {
      const response = await petAPI.getPetVisits(selectedPet.id);
      if (response.data.status === 'success') {
        const visits = response.data.data || [];
        // Only show visits that are checked out, completed, or closed
        const filtered = visits.filter(v =>
          !['active', 'admitted', 'registered', 'pending', 'in_progress', 'ongoing', 'cancelled', 'canceled'].includes(String(v.status || '').toLowerCase())
        );
        setPastVisits(filtered);
      } else if (Array.isArray(response.data)) {
        const filtered = response.data.filter(v =>
          !['active', 'admitted', 'registered', 'pending', 'in_progress', 'ongoing', 'cancelled', 'canceled'].includes(String(v.status || '').toLowerCase())
        );
        setPastVisits(filtered);
      }
    } catch (err) {
      console.error('Error fetching past visits:', err);
      setPastVisits([]);
    } finally {
      setLoadingPastVisits(false);
    }
  }, [selectedPet?.id]);

  // Fetch past visits when past-visits tab is selected
  useEffect(() => {
    if (selectedRecordType === 'past-visits' && selectedPet?.id) {
      fetchPastVisits();
    }
  }, [selectedRecordType, selectedPet?.id, fetchPastVisits]);

  // Clamp scroll so Overview tab can't scroll into a large blank area
  useEffect(() => {
    const el = guardianScrollRef.current;
    if (!el) return;

    const buffer = 16; // allow a tiny gap at the very bottom

    const handleScroll = () => {
      if (activeTab !== 'overview') return;
      const maxScrollTop = Math.max(0, el.scrollHeight - el.clientHeight - buffer);
      if (el.scrollTop > maxScrollTop) {
        el.scrollTop = maxScrollTop;
      }
    };

    el.addEventListener("scroll", handleScroll);
    return () => {
      el.removeEventListener("scroll", handleScroll);
    };
  }, [activeTab]);

  // Upcoming & Present: active, pending, confirmed, new, upcoming, or live visits
  const upcomingAppointments = appointments.filter(apt => {
    return ['pending', 'confirmed', 'new', 'upcoming', 'active', 'outpatient_visit', 'inpatient_visit', 'converted'].includes(String(apt.status || '').toLowerCase());
  });

  // Past: completed, no_show, cancelled, or anything else not in upcoming
  // Sort by date descending (most recent first)
  const pastAppointments = appointments
    .filter(apt => !upcomingAppointments.some(u => u.id === apt.id))
    .sort((a, b) => new Date(b.appointmentDateTime) - new Date(a.appointmentDateTime));



  // Handle appointment cancellation
  const handleCancelAppointment = (appointment) => {
    // Prevent cancellation of converted appointments
    if (appointment.isConverted) {
      showErrorToast(t("guardianDash.appointments.toasts.cannotCancelConverted") || "This appointment has been converted to a visit and cannot be cancelled.");
      return;
    }

    // Prevent cancellation if appointment time has passed
    const now = new Date();
    if (appointment.appointmentDateTime && appointment.appointmentDateTime < now) {
      showErrorToast(t("guardianDash.appointments.toasts.cannotCancelPast") || "Cannot cancel an appointment that has already passed.");
      return;
    }

    setAppoitmentToCancel(appointment);
    setShowCancelModal(true);
  };

  // Determine if a completed appointment should show the rating prompt
  // Only show if explicitly true from backend (defensive: backend may not have table yet)
  const canShowClinicRatingPrompt = (appointment) => {
    if (!appointment) return false;
    const status = String(appointment.status || "").toLowerCase();
    if (status !== "completed") return false;
    // Only show if explicitly true (not just truthy) - defensive against missing backend table
    return (
      appointment.canRateClinic === true ||
      appointment.can_rate_clinic === true ||
      appointment.has_pending_clinic_rating === true
    );
  };

  const openClinicRatingModal = (appointment) => {
    if (!appointment) return;
    setRatingModalAppointment(appointment);
    setRatingValue(0);
    setRatingComment("");
  };

  const closeClinicRatingModal = () => {
    setRatingModalAppointment(null);
    setRatingValue(0);
    setRatingComment("");
  };

  // User explicitly chooses to skip rating this appointment.
  // This only updates local state; backend can optionally clear the flag separately.
  const handleSkipClinicRating = (appointment) => {
    if (!appointment) return;
    setAppointments((prev) =>
      prev.map((apt) =>
        apt.id === appointment.id
          ? {
            ...apt,
            canRateClinic: false,
            can_rate_clinic: false,
            has_pending_clinic_rating: false,
          }
          : apt
      )
    );
  };

  const handleSubmitClinicRating = async (rating = ratingValue, comment = ratingComment) => {
    if (!ratingModalAppointment || rating < 1) {
      return;
    }

    try {
      setIsSubmittingRating(true);

      const token = localStorage.getItem("access_token");
      const API_BASE_URL =
        import.meta.env.VITE_API_URL || "http://localhost:8000";

      const response = await fetch(
        `${API_BASE_URL}/api/v1/clinics/${ratingModalAppointment.clinicId}/ratings`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            appointment_id: ratingModalAppointment.id,
            rating: rating,
            comment: (comment || "").trim() || undefined,
          }),
        }
      );

      if (!response.ok) {
        let message = t(
          "guardianDash.appointments.toasts.ratingError",
          "Failed to submit rating. Please try again."
        );

        try {
          const errorData = await response.json();

          // Handle 403 Forbidden (user is a member of the clinic)
          if (response.status === 403) {
            message = t(
              "guardianDash.appointments.toasts.cannotRateOwnClinic",
              "You cannot rate a clinic you are a member of."
            );

            // Check if backend sent a specific error message
            if (errorData?.message) {
              message = errorData.message;
            } else if (errorData?.errors?.clinic_id?.[0]) {
              message = errorData.errors.clinic_id[0];
            }
          }
          // Handle validation errors (422)
          else if (response.status === 422) {
            // Check for clinic_id validation error (from backend withValidator)
            if (errorData?.errors?.clinic_id?.[0]) {
              message = errorData.errors.clinic_id[0];
            } else if (errorData?.errors?.rating?.[0]) {
              message = errorData.errors.rating[0];
            } else if (errorData?.message) {
              message = errorData.message;
            }
          }
          // Handle other errors with backend message if available
          else if (errorData?.message) {
            message = errorData.message;
          }
        } catch (_) {
          // ignore JSON parse errors
        }

        // If endpoint doesn't exist (404) or server error (500), show friendly message
        if (response.status === 404 || response.status >= 500) {
          message = t(
            "guardianDash.appointments.toasts.ratingNotAvailable",
            "Rating feature is not available yet. Please try again later."
          );
        }

        throw new Error(message);
      }

      // Optimistically clear rating flag so the card unlocks
      setAppointments((prev) =>
        prev.map((apt) =>
          apt.id === ratingModalAppointment.id
            ? {
              ...apt,
              canRateClinic: false,
              can_rate_clinic: false,
              has_pending_clinic_rating: false,
            }
            : apt
        )
      );

      closeClinicRatingModal();
      showSuccessToast(
        t(
          "guardianDash.appointments.toasts.ratingSuccess",
          "Thanks for rating the clinic!"
        )
      );
    } catch (error) {
      console.error("Error submitting clinic rating:", error);
      showErrorToast(
        error.message ||
        t(
          "guardianDash.appointments.toasts.ratingError",
          "Failed to submit rating. Please try again."
        )
      );
    } finally {
      setIsSubmittingRating(false);
    }
  };

  // Confirm cancellation
  const confirmCancellation = async () => {
    if (!appointmentToCancel) return;

    // Final safeguard: prevent cancellation of converted appointments
    if (appointmentToCancel.isConverted) {
      showErrorToast(t("guardianDash.appointments.toasts.cannotCancelConverted") || "This appointment has been converted to a visit and cannot be cancelled.");
      setShowCancelModal(false);
      setAppoitmentToCancel(null);
      return;
    }

    // Final safeguard: prevent cancellation if appointment time has passed
    const now = new Date();
    if (appointmentToCancel.appointmentDateTime && appointmentToCancel.appointmentDateTime < now) {
      showErrorToast(t("guardianDash.appointments.toasts.cannotCancelPast") || "Cannot cancel an appointment that has already passed.");
      setShowCancelModal(false);
      setAppoitmentToCancel(null);
      return;
    }

    try {
      await clinicAPI.cancelAppointment(appointmentToCancel.id);

      // Remove the cancelled appointment from the list
      setAppointments(prevAppointments =>
        prevAppointments.filter(apt => apt.id !== appointmentToCancel.id)
      );

      // Close modal and reset state
      setShowCancelModal(false);
      setAppoitmentToCancel(null);

      // Show success message (we can replace this with a toast later)
      showSuccessToast(t("guardianDash.appointments.toasts.cancelSuccess"));
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      showErrorToast(t("guardianDash.appointments.toasts.cancelError"));
    }
  };

  // Close cancel modal
  const closeCancelModal = () => {
    setShowCancelModal(false);
    setAppoitmentToCancel(null);
  };

  // Handle appointment rescheduling
  const handleRescheduleAppointment = (appointment) => {
    // Prevent rescheduling of converted appointments
    if (appointment.isConverted) {
      showErrorToast(t("guardianDash.appointments.toasts.cannotRescheduleConverted") || "This appointment has been converted to a visit and cannot be rescheduled.");
      return;
    }

    // Prevent rescheduling if appointment time has passed
    const now = new Date();
    if (appointment.appointmentDateTime && appointment.appointmentDateTime < now) {
      showErrorToast(t("guardianDash.appointments.toasts.cannotReschedulePast") || "Cannot reschedule an appointment that has already passed.");
      return;
    }

    setAppointmentToReschedule(appointment);
    setSelectedDate('');
    setSelectedTime('');
    setAvailableTimeSlots([]);
    setShowRescheduleModal(true);

    // Initialize Flatpickr after modal opens, passing appointment data directly
    setTimeout(() => {
      initializeReschedulePicker(appointment);
    }, 100);
  };

  // Initialize Flatpickr for date selection only
  const initializeReschedulePicker = (appointment = appointmentToReschedule) => {
    if (reschedulePickerRef.current && !rescheduleFlatpickrInstance.current) {
      rescheduleFlatpickrInstance.current = flatpickr(reschedulePickerRef.current, {
        enableTime: false,
        dateFormat: "Y-m-d",
        minDate: "today",
        disableMobile: true,
        onChange: function (selectedDates, dateStr, instance) {
          if (selectedDates.length > 0) {
            setSelectedDate(dateStr);
            setSelectedTime(''); // Reset time selection
            fetchAvailableTimeSlotsWithAppointment(dateStr, appointment);
          }
        }
      });
    }
  };

  // Fetch available time slots with appointment data passed directly
  const fetchAvailableTimeSlotsWithAppointment = async (selectedDate, appointment) => {
    if (!appointment || !selectedDate) {
      return;
    }

    setLoadingTimeSlots(true);
    try {
      // Get clinic working hours
      const workingHoursResponse = await clinicAPI.getWorkingHours(appointment.clinicId);
      const workingHours = workingHoursResponse.data;

      // Get day of week for selected date
      const selectedDateObj = new Date(selectedDate);
      const dayOfWeek = selectedDateObj.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();

      // Find working hours for this day
      const dayWorkingHours = workingHours.find(wh => wh.day === dayOfWeek);

      if (!dayWorkingHours || !dayWorkingHours.enabled) {
        setAvailableTimeSlots([]);
        return;
      }

      // Generate time slots based on working hours
      const timeSlots = generateTimeSlotsFromWorkingHours(dayWorkingHours);
      setAvailableTimeSlots(timeSlots);

    } catch (error) {
      console.error('Error fetching time slots:', error);
      // Fallback to mock data if API fails
      const mockTimeSlots = generateMockTimeSlots();
      setAvailableTimeSlots(mockTimeSlots);
    } finally {
      setLoadingTimeSlots(false);
    }
  };

  // Fetch available time slots for selected date
  const fetchAvailableTimeSlots = async (selectedDate) => {
    if (!appointmentToReschedule || !selectedDate) {
      return;
    }

    setLoadingTimeSlots(true);
    try {
      // Get clinic working hours
      const workingHoursResponse = await clinicAPI.getWorkingHours(appointmentToReschedule.clinicId);
      const workingHours = workingHoursResponse.data;

      // Get day of week for selected date
      const selectedDateObj = new Date(selectedDate);
      const dayOfWeek = selectedDateObj.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();

      // Find working hours for this day
      const dayWorkingHours = workingHours.find(wh => wh.day === dayOfWeek);

      if (!dayWorkingHours || !dayWorkingHours.enabled) {
        setAvailableTimeSlots([]);
        return;
      }

      // Generate time slots based on working hours
      const timeSlots = generateTimeSlotsFromWorkingHours(dayWorkingHours);
      setAvailableTimeSlots(timeSlots);

    } catch (error) {
      console.error('Error fetching time slots:', error);
      // Fallback to mock data if API fails
      const mockTimeSlots = generateMockTimeSlots();
      setAvailableTimeSlots(mockTimeSlots);
    } finally {
      setLoadingTimeSlots(false);
    }
  };

  // Generate time slots based on clinic working hours
  const generateTimeSlotsFromWorkingHours = (workingHours) => {
    const slots = [];
    const startTime = workingHours.from; // e.g., "09:00"
    const endTime = workingHours.to; // e.g., "17:00"

    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);

    const startMinutes = startHour * 60 + startMinute;
    const endMinutes = endHour * 60 + endMinute;

    // Generate 30-minute intervals
    for (let minutes = startMinutes; minutes < endMinutes; minutes += 30) {
      const hour = Math.floor(minutes / 60);
      const minute = minutes % 60;

      const time24 = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      const timeObj = new Date(`2000-01-01T${time24}:00`);
      const time12 = timeObj.toLocaleTimeString(locale, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });

      slots.push({
        value: time24,
        label: time12,
        available: true // All slots are available for now - could add booking conflict checking here
      });
    }

    return slots;
  };

  // Fallback mock time slots
  const generateMockTimeSlots = () => {
    const slots = [];
    const startHour = 9; // 9 AM
    const endHour = 17; // 5 PM

    for (let hour = startHour; hour < endHour; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const time24 = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const timeObj = new Date(`2000-01-01T${time24}:00`);
        const time12 = timeObj.toLocaleTimeString(locale, {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });

        slots.push({
          value: time24,
          label: time12,
          available: true
        });
      }
    }

    return slots;
  };



  // Show reschedule preview
  const showReschedulePreviewModal = () => {
    if (!appointmentToReschedule || !selectedDate || !selectedTime) {
      showActionToast(t("guardianDash.rescheduleModal.notifications.selectDateTime"));
      return;
    }

    // Hide the reschedule modal and show preview
    setShowRescheduleModal(false);
    setShowReschedulePreview(true);
  };

  // Finalize reschedule (actual API call)
  const finalizeReschedule = async () => {
    if (!appointmentToReschedule || !selectedDate || !selectedTime) {
      showActionToast(t("guardianDash.rescheduleModal.notifications.selectDateTime"));
      return;
    }

    try {
      // Use local date-time string format to match the working AppointmentRequest flow
      const localDateTimeStr = `${selectedDate} ${selectedTime}`;

      // Prepare the reschedule data with all required fields
      const rescheduleData = {
        clinic_id: appointmentToReschedule.clinicId,
        vet_id: appointmentToReschedule.vetId,
        pet_owner_id: appointmentToReschedule.petOwnerId,
        appointment_type_id: appointmentToReschedule.appointmentTypeId,
        appointment_at: localDateTimeStr
      };
      await clinicAPI.rescheduleAppointment(appointmentToReschedule.id, rescheduleData);

      // Update the appointment in the local state
      const newDateTime = new Date(`${selectedDate} ${selectedTime}`);
      setAppointments(prevAppointments =>
        prevAppointments.map(apt =>
          apt.id === appointmentToReschedule.id
            ? {
              ...apt,
              date: selectedDate,
              time: newDateTime.toLocaleTimeString(locale, {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
              })
            }
            : apt
        )
      );

      // Close modals and reset state
      closeRescheduleModal();
      setShowReschedulePreview(false);

      showSuccessToast(t("guardianDash.rescheduleModal.toasts.success"));
    } catch (error) {
      console.error('Error rescheduling appointment:', error);
      showErrorToast(t("guardianDash.rescheduleModal.toasts.error"));
    }
  };

  // Close reschedule modal
  const closeRescheduleModal = () => {
    // Destroy Flatpickr instance
    if (rescheduleFlatpickrInstance.current) {
      rescheduleFlatpickrInstance.current.destroy();
      rescheduleFlatpickrInstance.current = null;
    }

    setShowRescheduleModal(false);
    setAppointmentToReschedule(null);
    setSelectedDate('');
    setSelectedTime('');
    setAvailableTimeSlots([]);
    setLoadingTimeSlots(false);
  };

  // Close preview modal and go back to reschedule modal
  const goBackToReschedule = () => {
    setShowReschedulePreview(false);
    setShowRescheduleModal(true);
  };

  // Close all reschedule-related modals
  const cancelReschedule = () => {
    setShowReschedulePreview(false);
    closeRescheduleModal();
  };

  return (
    <div
      className={`guardian-dash-root ${isRTL ? "rtl" : ""}`}
      dir={i18n.dir()}
      lang={i18n.language}
    >
      <Navbar />
      {/* Scrollable content area inside the fixed-height root */}
      <div className="guardian-dash-scroll" ref={guardianScrollRef}>
        {/* Tab Navigation */}
        <div className="guardian-dash-tabs guardian-dash-tabs-responsive">
          {userType !== 'FIRM' && (
            <button
              className={`tab-btn ${activeTab === 'sos' ? 'active' : ''}`}
              onClick={() => handleTabChange('sos')}
            >
              {t("guardianDash.tabs.sos") || 'S.O.S'}
            </button>
          )}
          <button
            className={`tab-btn ${activeTab === 'appointments' ? 'active' : ''}`}
            onClick={() => handleTabChange('appointments')}
          >
            {t("guardianDash.tabs.appointments")}
          </button>
          <button
            className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => handleTabChange('overview')}
          >
            {t("guardianDash.tabs.overview")}
          </button>
        </div>

        {/* Tab Content */}
        <div className="guardian-dash-tab-content">
          {activeTab === 'overview' ? (
            <section className="guardian-dash-medical-records">
              <h2>{t("guardianDash.records.title")}</h2>

              {/* Pet Selector Cards */}
              {pets.length > 0 && (
                <div className="pet-selector-wrapper" style={{ marginBottom: '30px' }}>
                  <h3 className="pet-selector-title">{t("guardianDash.petSelector.title")}</h3>
                  <div className="pet-cards-container pet-cards-container-responsive">
                    {pets.map(pet => (
                      <div
                        key={pet.id}
                        onClick={() => setSelectedPet(pet)}
                        className={`pet-card ${selectedPet?.id === pet.id ? 'selected' : ''}`}
                      >
                        {/* Pet Image */}
                        <div className="pet-avatar">
                          {resolvePetImage(pet) ? (
                            <img
                              src={resolvePetImage(pet)}
                              alt={
                                typeof pet.name === "string"
                                  ? pet.name
                                  : pet.name?.en ||
                                  pet.name?.ar ||
                                  t("guardianDash.pet.fallback.alt")
                              }
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div className="pet-avatar-fallback" style={{
                            display: resolvePetImage(pet) ? 'none' : 'flex'
                          }}>
                            {pet.species === 'Dog' ? '🐕' : pet.species === 'Cat' ? '🐱' : '🐾'}
                          </div>
                        </div>

                        {/* Pet Info */}
                        <div className="pet-info">
                          <div className="pet-name">
                            {typeof pet.name === 'string'
                              ? pet.name
                              : pet.name?.en ||
                              pet.name?.ar ||
                              t("guardianDash.pet.fallback.name")}
                          </div>
                          <div className="pet-details">
                            {typeof pet.species === 'string'
                              ? pet.species
                              : pet.species?.en ||
                              pet.species?.ar ||
                              t("guardianDash.pet.fallback.species")}
                            {" "}
                            •{" "}
                            {typeof pet.breed === 'string'
                              ? pet.breed
                              : pet.breed?.en ||
                              pet.breed?.ar ||
                              t("guardianDash.pet.fallback.breed")}
                            {" "}
                            •{" "}
                            {typeof pet.age === 'string'
                              ? pet.age
                              : String(pet.age) || t("guardianDash.pet.fallback.age")}
                          </div>
                        </div>

                        {/* Selected Indicator */}
                        {selectedPet?.id === pet.id && (
                          <div className="pet-selected-indicator">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                              <polyline points="20,6 9,17 4,12" />
                            </svg>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Medical Record Type Selector */}
              {selectedPet && (
                <div className="record-type-selector-wrapper" style={{ marginBottom: '30px' }}>
                  <div className="record-type-buttons record-type-buttons-responsive">
                    {[
                      { key: 'vaccinations', title: t("guardianDash.recordTypes.options.vaccinations"), icon: '/icons/vacc.png' },
                      { key: 'medications', title: t("guardianDash.recordTypes.options.medications"), icon: '/icons/med.png' },
                      { key: 'allergies', title: t("guardianDash.recordTypes.options.allergies"), icon: '/icons/aller.png' },
                      { key: 'past-illness-history', title: t("guardianDash.recordTypes.options.pastIllnesses"), icon: '/icons/chron.png' },
                      { key: 'surgeries', title: t("guardianDash.recordTypes.options.surgeries"), icon: '/icons/surg.png' },
                      { key: 'dental-records', title: t("guardianDash.recordTypes.options.dentalRecords"), icon: '/icons/dent.png' },
                      { key: 'deworming', title: t("guardianDash.recordTypes.options.deworming"), icon: '/icons/vacc.png' },
                      { key: 'past-visits', title: t("receptionInpatients.details.tabs.pastVisits", { defaultValue: 'Past Visits' }), icon: '/icons/chron.png' }
                    ].map(recordType => (
                      <button
                        key={recordType.key}
                        onClick={() => setSelectedRecordType(recordType.key)}
                        className={`record-type-btn ${selectedRecordType === recordType.key ? 'selected' : ''}`}
                      >
                        <img
                          src={recordType.icon}
                          alt={recordType.title}
                          className="record-type-icon"
                        />
                        {recordType.title}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Single Medical Record Component - render only the active record type to avoid extra hidden height */}
              {selectedPet && selectedRecordType ? (
                <div
                  className="medical-record-content"
                  key={`pet-records-${selectedPet?.id}-${selectedRecordType}`}
                >
                  {selectedRecordType === 'vaccinations' && (
                    <MedicalRecordView
                      key={`vaccinations-${selectedPet?.id}-${selectedRecordType}`}
                      title={t("guardianDash.recordViews.vaccinations.title")}
                      icon="/icons/vacc.png"
                      columns={[
                        { field: "Veterinarian", label: t("guardianDash.recordViews.vaccinations.columns.veterinarian") },
                        { field: "Vaccine Name", label: t("guardianDash.recordViews.vaccinations.columns.vaccineName") },
                        { field: "Date Administered", label: t("guardianDash.recordViews.vaccinations.columns.dateAdministered") },
                        { field: "Next Due Date", label: t("guardianDash.recordViews.vaccinations.columns.nextDueDate") },
                        { field: "Note", label: t("guardianDash.recordViews.vaccinations.columns.note") },
                      ]}
                      fetchUrl="/api/v1/pet-health/vaccinations"
                      formType="vaccination"
                      petData={selectedPet}
                      showActions={false}
                    />
                  )}

                  {selectedRecordType === 'medications' && (
                    <MedicalRecordView
                      key={`medications-${selectedPet?.id}-${selectedRecordType}`}
                      title={t("guardianDash.recordViews.medications.title")}
                      icon="/icons/med.png"
                      columns={[
                        { field: "Veterinarian", label: t("guardianDash.recordViews.medications.columns.veterinarian") },
                        { field: "Medication Name", label: t("guardianDash.recordViews.medications.columns.medicationName") },
                        { field: "Dosage", label: t("guardianDash.recordViews.medications.columns.dosage") },
                        { field: "Start Date", label: t("guardianDash.recordViews.medications.columns.startDate") },
                        { field: "Currently Taking", label: t("guardianDash.recordViews.medications.columns.currentlyTaking") },
                      ]}
                      fetchUrl="/api/v1/pet-health/medications"
                      formType="medication"
                      petData={selectedPet}
                      showActions={false}
                    />
                  )}

                  {selectedRecordType === 'allergies' && (
                    <MedicalRecordView
                      key={`allergies-${selectedPet?.id}-${selectedRecordType}`}
                      title={t("guardianDash.recordViews.allergies.title")}
                      icon="/icons/aller.png"
                      columns={[
                        { field: "Allergen", label: t("guardianDash.recordViews.allergies.columns.allergen") },
                        { field: "Reaction", label: t("guardianDash.recordViews.allergies.columns.reaction") },
                        { field: "Severity", label: t("guardianDash.recordViews.allergies.columns.severity") },
                        { field: "Note", label: t("guardianDash.recordViews.allergies.columns.note") },
                      ]}
                      fetchUrl="/api/v1/pet-health/allergies"
                      formType="allergies"
                      petData={selectedPet}
                      showActions={false}
                    />
                  )}

                  {selectedRecordType === 'past-illness-history' && (
                    <MedicalRecordView
                      key={`past-illness-history-${selectedPet?.id}-${selectedRecordType}`}
                      title={t("guardianDash.recordViews.pastIllnesses.title")}
                      icon="/icons/chron.png"
                      columns={[
                        { field: "Veterinarian", label: t("guardianDash.recordViews.pastIllnesses.columns.veterinarian") },
                        { field: "Final Diagnosis", label: t("petProfile.medicalRecords.columns.finalDiagnosis") },
                        { field: "Diagnosis Date", label: t("guardianDash.recordViews.pastIllnesses.columns.diagnosisDate") },
                        { field: "Actions", label: t("petProfile.medicalRecords.columns.actions") },
                      ]}
                      fetchUrl="/api/v1/pet-health/past-illness-history"
                      formType="pastIllness"
                      petData={selectedPet}
                      showActions={false}
                    />
                  )}

                  {selectedRecordType === 'surgeries' && (
                    <MedicalRecordView
                      key={`surgeries-${selectedPet?.id}-${selectedRecordType}`}
                      title={t("guardianDash.recordViews.surgeries.title")}
                      icon="/icons/surg.png"
                      columns={[
                        { field: "Veterinarian", label: t("guardianDash.recordViews.surgeries.columns.veterinarian") },
                        { field: "Surgery Name", label: t("guardianDash.recordViews.surgeries.columns.surgeryName") },
                        { field: "Note", label: t("guardianDash.recordViews.surgeries.columns.note") },
                        { field: "Date", label: t("guardianDash.recordViews.surgeries.columns.date") },
                      ]}
                      fetchUrl="/api/v1/pet-health/surgeries"
                      formType="surgery"
                      petData={selectedPet}
                      showActions={false}
                    />
                  )}

                  {selectedRecordType === 'dental-records' && (
                    <MedicalRecordView
                      key={`dental-records-${selectedPet?.id}-${selectedRecordType}`}
                      title={t("guardianDash.recordViews.dental.title")}
                      icon="/icons/dent.png"
                      columns={[
                        { field: "Veterinarian", label: t("guardianDash.recordViews.dental.columns.veterinarian") },
                        { field: "Dental Procedure", label: t("guardianDash.recordViews.dental.columns.procedure") },
                        { field: "Note", label: t("guardianDash.recordViews.dental.columns.note") },
                        { field: "Date", label: t("guardianDash.recordViews.dental.columns.date") },
                      ]}
                      fetchUrl="/api/v1/pet-health/dental-records"
                      formType="dental"
                      petData={selectedPet}
                      showActions={false}
                    />
                  )}

                  {selectedRecordType === 'deworming' && (
                    <MedicalRecordView
                      key={`deworming-${selectedPet?.id}-${selectedRecordType}`}
                      title={t("guardianDash.recordViews.deworming.title")}
                      icon="/icons/vacc.png"
                      columns={[
                        { field: "Veterinarian", label: t("guardianDash.recordViews.deworming.columns.veterinarian") },
                        { field: "Type", label: t("guardianDash.recordViews.deworming.columns.type") },
                        { field: "Date", label: t("guardianDash.recordViews.deworming.columns.date") },
                        { field: "Next Due Date", label: t("guardianDash.recordViews.deworming.columns.nextDueDate") },
                        { field: "Note", label: t("guardianDash.recordViews.deworming.columns.note") },
                      ]}
                      fetchUrl="/api/v1/pet-health/deworming-anti-flea-history"
                      formType="dewormingAntiFlea"
                      petData={selectedPet}
                      showActions={false}
                    />
                  )}

                  {selectedRecordType === 'past-visits' && (
                    <div style={{ animation: 'fadeInUp 180ms ease-out' }}>
                      {loadingPastVisits ? (
                        <div style={{ padding: '40px', textAlign: 'center' }}>
                          <div style={{ marginBottom: '16px' }}>{t('common.loading', { defaultValue: 'Loading...' })}</div>
                        </div>
                      ) : pastVisits.length === 0 ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>
                          <p>{t('receptionOutpatients.medicalHistory.sections.pastVisits.empty', { defaultValue: 'No past visits found for this pet.' })}</p>
                        </div>
                      ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                          {pastVisits.map((visit) => {
                            const formatDate = (dateString) => {
                              if (!dateString) return '---';
                              try {
                                const date = new Date(dateString);
                                if (isNaN(date.getTime())) return dateString;
                                return date.toLocaleDateString(locale, {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                });
                              } catch {
                                return dateString;
                              }
                            };

                            return (
                              <div
                                key={visit.id || visit.visit_id || visit.inpatient_visit_id || visit.outpatient_visit_id}
                                onClick={() => setSelectedVisit(visit)}
                                style={{
                                  padding: '20px',
                                  backgroundColor: '#ffffff',
                                  borderRadius: '12px',
                                  border: '1px solid #E5E7EB',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.borderColor = '#8548A8';
                                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(133, 72, 168, 0.15)';
                                  e.currentTarget.style.transform = 'translateY(-2px)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.borderColor = '#E5E7EB';
                                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                }}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                                  <div>
                                    <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#111827', marginBottom: '4px' }}>
                                      {String(visit.type || '').toLowerCase() === 'inpatient' 
                                        ? t('receptionInpatients.title', { defaultValue: 'Inpatient Visit' }) 
                                        : t('receptionOutpatients.title', { defaultValue: 'Outpatient Visit' })}
                                    </h4>
                                    <p style={{ margin: 0, fontSize: '14px', color: '#6B7280' }}>
                                      {formatDate(visit.visit_date || visit.created_at)}
                                    </p>
                                  </div>
                                  <div style={{
                                    padding: '4px 8px',
                                    backgroundColor: '#F3F4F6',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    color: '#6B7280'
                                  }}>
                                    {visit.status || t('common.statusCompleted', { defaultValue: 'Completed' })}
                                  </div>
                                </div>
                                {visit.reason && (
                                  <p style={{ margin: 0, fontSize: '14px', color: '#374151', marginTop: '8px', lineHeight: '1.5' }}>
                                    <strong>{t('receptionOutpatients.medicalHistory.sections.pastVisits.fields.reason', { defaultValue: 'Reason:' })}</strong> {visit.reason}
                                  </p>
                                )}
                                {visit.final_diagnosis && (
                                  <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#374151', lineHeight: '1.5' }}>
                                    <strong>{t('petProfile.medicalRecords.columns.finalDiagnosis', { defaultValue: 'Diagnosis:' })}</strong> {visit.final_diagnosis}
                                  </p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : selectedPet ? (
                <div className="empty-state empty-state-dashed">
                  <h3>{t("guardianDash.emptyStates.selectRecordType.title")}</h3>
                  <p>
                    {t("guardianDash.emptyStates.selectRecordType.description", {
                      petName:
                        typeof selectedPet.name === "string"
                          ? selectedPet.name
                          : selectedPet.name?.en ||
                          selectedPet.name?.ar ||
                          t("guardianDash.pet.fallback.possessive"),
                    })}
                  </p>
                </div>
              ) : (
                <div className="empty-state">
                  <h3>{t("guardianDash.emptyStates.noPets.title")}</h3>
                  <p>{t("guardianDash.emptyStates.noPets.description")}</p>
                  <button
                    onClick={() => navigate('/pets/add')}
                    className="add-pet-btn"
                  >
                    {t("guardianDash.emptyStates.noPets.cta")}
                  </button>
                </div>
              )}
            </section>
          ) : activeTab === 'appointments' ? (
            <section className="guardian-dash-medical-records">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', flexWrap: 'wrap', gap: '16px' }}>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <h2>{t("guardianDash.appointments.title")}</h2>
                  <p>{t("guardianDash.appointments.description")}</p>
                </div>
                <button
                  className="sos-secondary-btn"
                  onClick={() => navigate(getAppointmentRequestUrl())}
                  style={{
                    marginTop: '8px',
                    whiteSpace: 'nowrap',
                    flexShrink: 0
                  }}
                >
                  <span className="btn-icon">📅</span>
                  {t("guardianDash.sos.secondary") || "Request an appointment"}
                </button>
              </div>

              {/* Pet Selector Cards for Appointments */}
              {pets.length > 0 && (
                <div style={{ marginBottom: '30px' }}>
                  <h3 className="pet-selector-title">{t("guardianDash.appointments.petSelectorTitle")}</h3>
                  <div className="pet-cards-container">
                    {/* All Pets Option */}
                    <div
                      onClick={() => setSelectedPetForAppointments(null)}
                      className={`pet-card ${selectedPetForAppointments === null ? 'selected' : ''}`}
                    >
                      <div className="pet-avatar">
                        <div className="pet-avatar-fallback" style={{ display: 'flex' }}>
                          🐾
                        </div>
                      </div>
                      <div className="pet-info">
                        <div className="pet-name">{t("guardianDash.appointments.petFilters.allPets")}</div>
                        <div className="pet-details">{t("guardianDash.appointments.petFilters.allPetsDescription")}</div>
                      </div>
                      {selectedPetForAppointments === null && (
                        <div className="pet-selected-indicator">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                            <polyline points="20,6 9,17 4,12" />
                          </svg>
                        </div>
                      )}
                    </div>

                    {pets.map(pet => (
                      <div
                        key={pet.id}
                        onClick={() => setSelectedPetForAppointments(pet)}
                        className={`pet-card ${selectedPetForAppointments?.id === pet.id ? 'selected' : ''}`}
                      >
                        {/* Pet Image */}
                        <div className="pet-avatar">
                          {resolvePetImage(pet) ? (
                            <img
                              src={resolvePetImage(pet)}
                              alt={typeof pet.name === 'string' ? pet.name : (pet.name?.en || pet.name?.ar || 'Pet')}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div className="pet-avatar-fallback" style={{
                            display: resolvePetImage(pet) ? 'none' : 'flex'
                          }}>
                            {pet.species === 'Dog' ? '🐕' : pet.species === 'Cat' ? '🐱' : '🐾'}
                          </div>
                        </div>

                        {/* Pet Info */}
                        <div className="pet-info">
                          <div className="pet-name">
                            {typeof pet.name === 'string'
                              ? pet.name
                              : pet.name?.en ||
                              pet.name?.ar ||
                              t("guardianDash.pet.fallback.name")}
                          </div>
                          <div className="pet-details">
                            {typeof pet.species === 'string'
                              ? pet.species
                              : pet.species?.en ||
                              pet.species?.ar ||
                              t("guardianDash.pet.fallback.species")}
                            {" "}
                            •{" "}
                            {typeof pet.breed === 'string'
                              ? pet.breed
                              : pet.breed?.en ||
                              pet.breed?.ar ||
                              t("guardianDash.pet.fallback.breed")}
                            {" "}
                            •{" "}
                            {typeof pet.age === 'string'
                              ? pet.age
                              : String(pet.age) || t("guardianDash.pet.fallback.age")}
                          </div>
                        </div>

                        {/* Selected Indicator */}
                        {selectedPetForAppointments?.id === pet.id && (
                          <div className="pet-selected-indicator">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                              <polyline points="20,6 9,17 4,12" />
                            </svg>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Animated Appointments Content */}
              <div
                className="appointments-content appointments-content-responsive"
                key={`appointments-${selectedPetForAppointments?.id || 'all'}`}
              >
                {loading ? (
                  <div className="loading-state">
                    <p>{t("guardianDash.appointments.loading")}</p>
                  </div>
                ) : error ? (
                  <div className="error-state">
                    <p style={{ color: '#e74c3c' }}>{error}</p>
                  </div>
                ) : (
                  <>
                    {/* Upcoming Appointments */}
                    {(() => {
                      const filteredUpcoming = upcomingAppointments.filter(appointment => {
                        const matches = selectedPetForAppointments === null ||
                          String(appointment.petId) === String(selectedPetForAppointments?.id);
                        return matches;
                      });
                      return filteredUpcoming.length > 0;
                    })() && (
                        <div className="appointments-section">
                          <h3>
                            {selectedPetForAppointments
                              ? t("guardianDash.appointments.sections.upcomingWithPet", {
                                petName: resolvePetName(selectedPetForAppointments),
                              })
                              : t("guardianDash.appointments.sections.upcoming")}
                          </h3>
                          <div className="appointments-grid">
                            {upcomingAppointments.filter(appointment =>
                              selectedPetForAppointments === null || String(appointment.petId) === String(selectedPetForAppointments?.id)
                            ).map(appointment => (
                              <div
                                key={appointment.id}
                                className="appointment-card upcoming"
                                data-appointment-id={appointment.id}
                              >
                                <div className="appointment-header">
                                  <div className="pet-avatar">
                                    {resolvePetImage(appointment.pet) ? (
                                      <img
                                        src={resolvePetImage(appointment.pet)}
                                        alt={appointment.petName}
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                          e.target.nextSibling.style.display = 'flex';
                                        }}
                                      />
                                    ) : null}
                                    <div className="pet-avatar-fallback" style={{
                                      display: resolvePetImage(appointment.pet) ? 'none' : 'flex'
                                    }}>
                                      {appointment.pet?.species === 'Dog' ? '🐕' : appointment.pet?.species === 'Cat' ? '🐱' : '🐾'}
                                    </div>
                                  </div>
                                  <div className="appointment-info">
                                    <h4>{appointment.petName}</h4>
                                    <p className="appointment-type">{appointment.type}</p>
                                  </div>
                                  <span className={`status-badge ${appointment.status}`}>
                                    {t(`guardianDash.appointments.status.${appointment.status}`, {
                                      defaultValue: appointment.status,
                                    })}
                                  </span>
                                </div>
                                <div className="appointment-details">
                                  <div className="detail-row">
                                    <span className="label">{t("guardianDash.appointments.labels.clinic")}:</span>
                                    <span className="value">{appointment.clinicName}</span>
                                  </div>
                                  <div className="detail-row">
                                    <span className="label">{t("guardianDash.appointments.labels.veterinarian")}:</span>
                                    <span className="value">{appointment.vetName}</span>
                                  </div>
                                  <div className="detail-row">
                                    <span className="label">{t("guardianDash.appointments.labels.date")}:</span>
                                    <span className="value">{appointment.date}</span>
                                  </div>
                                  <div className="detail-row">
                                    <span className="label">{t("guardianDash.appointments.labels.time")}:</span>
                                    <span className="value">{appointment.time}</span>
                                  </div>
                                </div>
                                <div className="appointment-actions">
                                  {(() => {
                                    const now = new Date();
                                    const isPast = appointment.appointmentDateTime && appointment.appointmentDateTime < now;
                                    const isDisabled = appointment.isConverted || isPast;
                                    const tooltip = appointment.isConverted
                                      ? (appointment.visitType === 'inpatient'
                                        ? t("guardianDash.appointments.tooltips.cannotRescheduleInpatient")
                                        : t("guardianDash.appointments.tooltips.cannotRescheduleConverted"))
                                      : isPast
                                        ? t("guardianDash.appointments.tooltips.cannotReschedulePast")
                                        : "";

                                    return (
                                      <>
                                        <button
                                          className="btn-secondary"
                                          onClick={() => handleRescheduleAppointment(appointment)}
                                          disabled={isDisabled}
                                          style={{
                                            opacity: isDisabled ? 0.5 : 1,
                                            cursor: isDisabled ? 'not-allowed' : 'pointer'
                                          }}
                                          title={tooltip}
                                        >
                                          {t("guardianDash.appointments.actions.reschedule")}
                                        </button>
                                        <button
                                          className="btn-danger"
                                          onClick={() => handleCancelAppointment(appointment)}
                                          disabled={isDisabled}
                                          style={{
                                            opacity: isDisabled ? 0.5 : 1,
                                            cursor: isDisabled ? 'not-allowed' : 'pointer'
                                          }}
                                          title={appointment.isConverted
                                            ? (appointment.visitType === 'inpatient'
                                              ? t("guardianDash.appointments.tooltips.cannotCancelInpatient")
                                              : t("guardianDash.appointments.tooltips.cannotCancelConverted"))
                                            : isPast
                                              ? t("guardianDash.appointments.tooltips.cannotCancelPast")
                                              : ""}
                                        >
                                          {t("guardianDash.appointments.actions.cancel")}
                                        </button>
                                      </>
                                    );
                                  })()}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                    {/* Past Appointments */}
                    {(() => {
                      const filteredPast = pastAppointments.filter(appointment => {
                        const matches = selectedPetForAppointments === null ||
                          String(appointment.petId) === String(selectedPetForAppointments?.id);
                        return matches;
                      });
                      return filteredPast.length > 0;
                    })() && (
                        <div className="appointments-section">
                          <h3>
                            {selectedPetForAppointments
                              ? t("guardianDash.appointments.sections.recentWithPet", {
                                petName: resolvePetName(selectedPetForAppointments),
                              })
                              : t("guardianDash.appointments.sections.recent")}
                          </h3>
                          <div className="appointments-grid">
                            {pastAppointments
                              .filter(
                                (appointment) =>
                                  selectedPetForAppointments === null ||
                                  String(appointment.petId) ===
                                  String(selectedPetForAppointments?.id)
                              )
                              .map((appointment) => {
                                const showRatingPrompt =
                                  canShowClinicRatingPrompt(appointment);

                                return (
                                  <div
                                    key={appointment.id}
                                    className="appointment-card completed"
                                    data-appointment-id={appointment.id}
                                  >
                                    <div className="appointment-header">
                                      <div className="pet-avatar">
                                        {resolvePetImage(appointment.pet) ? (
                                          <img
                                            src={resolvePetImage(appointment.pet)}
                                            alt={appointment.petName}
                                            onError={(e) => {
                                              e.target.style.display = "none";
                                              e.target.nextSibling.style.display =
                                                "flex";
                                            }}
                                          />
                                        ) : null}
                                        <div
                                          className="pet-avatar-fallback"
                                          style={{
                                            display: resolvePetImage(appointment.pet)
                                              ? "none"
                                              : "flex",
                                          }}
                                        >
                                          {appointment.pet?.species === "Dog"
                                            ? "🐕"
                                            : appointment.pet?.species === "Cat"
                                              ? "🐱"
                                              : "🐾"}
                                        </div>
                                      </div>
                                      <div className="appointment-info">
                                        <h4>{appointment.petName}</h4>
                                        <p className="appointment-type">
                                          {appointment.type}
                                        </p>
                                      </div>
                                      <span
                                        className={`status-badge ${appointment.status}`}
                                      >
                                        {t(
                                          `guardianDash.appointments.status.${appointment.status}`,
                                          {
                                            defaultValue: appointment.status,
                                          }
                                        )}
                                      </span>
                                    </div>
                                    <div
                                      className={`appointment-details${showRatingPrompt
                                        ? " appointment-details-blurred"
                                        : ""
                                        }`}
                                    >
                                      <div className="detail-row">
                                        <span className="label">
                                          {t(
                                            "guardianDash.appointments.labels.clinic"
                                          )}
                                          :
                                        </span>
                                        <span className="value">
                                          {appointment.clinicName}
                                        </span>
                                      </div>
                                      <div className="detail-row">
                                        <span className="label">
                                          {t(
                                            "guardianDash.appointments.labels.veterinarian"
                                          )}
                                          :
                                        </span>
                                        <span className="value">
                                          {appointment.vetName}
                                        </span>
                                      </div>
                                      <div className="detail-row">
                                        <span className="label">
                                          {t("guardianDash.appointments.labels.date")}:
                                        </span>
                                        <span className="value">
                                          {appointment.date}
                                        </span>
                                      </div>
                                      <div className="detail-row">
                                        <span className="label">
                                          {t("guardianDash.appointments.labels.time")}:
                                        </span>
                                        <span className="value">
                                          {appointment.time}
                                        </span>
                                      </div>
                                    </div>

                                    {showRatingPrompt && (
                                      <div className="appointment-rating-prompt">
                                        <p className="appointment-rating-text">
                                          {t(
                                            "guardianDash.appointments.rating.prompt",
                                            {
                                              clinicName: appointment.clinicName,
                                            }
                                          )}
                                        </p>
                                        <div className="appointment-rating-actions">
                                          <button
                                            type="button"
                                            className="btn-primary"
                                            onClick={() =>
                                              openClinicRatingModal(appointment)
                                            }
                                          >
                                            {t(
                                              "guardianDash.appointments.rating.rate"
                                            )}
                                          </button>
                                          <button
                                            type="button"
                                            className="btn-secondary"
                                            onClick={() =>
                                              handleSkipClinicRating(appointment)
                                            }
                                          >
                                            {t(
                                              "guardianDash.appointments.rating.skip"
                                            )}
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      )}

                    {/* No Appointments */}
                    {([...upcomingAppointments, ...pastAppointments].filter(appointment =>
                      selectedPetForAppointments === null ||
                      String(appointment.petId) === String(selectedPetForAppointments?.id)
                    ).length === 0) && (
                        <div className="no-appointments">
                          <p>
                            {selectedPetForAppointments
                              ? t("guardianDash.appointments.empty.withPet", {
                                petName: resolvePetName(selectedPetForAppointments),
                              })
                              : t("guardianDash.appointments.empty.allPets")}
                          </p>
                          <button
                            className="btn-primary"
                            onClick={() => navigate(getAppointmentRequestUrl())}
                          >
                            {t("guardianDash.appointments.empty.cta")}
                          </button>
                        </div>
                      )}
                  </>
                )}
              </div>
            </section>
          ) : (
            <section className="guardian-dash-medical-records guardian-dash-sos">
              <h2>{t("guardianDash.sos.title")}</h2>
              <p>
                {t("guardianDash.sos.subtitle")}
              </p>

              <div className="guardian-dash-sos-card">
                <div className="guardian-dash-sos-main">
                  <div className="sos-header">
                    <span className="sos-label">
                      {t("guardianDash.sos.label")}
                    </span>
                    <h3>
                      {t("guardianDash.sos.heading")}
                    </h3>
                    <p>
                      {t("guardianDash.sos.description")}
                    </p>
                  </div>

                  <div className="sos-actions">
                    <button
                      className="sos-primary-btn"
                      onClick={() => navigate(getUrgentCaseSubmitUrl())}
                    >
                      <span className="btn-icon">🚨</span>
                      {t("guardianDash.sos.primary")}
                    </button>
                  </div>

                  <div className="sos-note">
                    {t("guardianDash.sos.note")}
                  </div>
                </div>

                <div className="guardian-dash-sos-figure">
                  <img
                    src="/images/medical/SulhafaVetPicture.png"
                    alt={
                      t("guardianDash.sos.imageAlt") ||
                      t("guardianDash.hero.imageAlt")
                    }
                  />
                </div>
              </div>
            </section>
          )}
        </div>
      </div>

      {/* Cancel Confirmation Modal */}
      {showCancelModal && appointmentToCancel && (
        <div className="modal-overlay">
          <div className="cancel-modal">
            <div className="modal-header">
              <h3>{t("guardianDash.cancelModal.title")}</h3>
              <button
                className="close-btn"
                onClick={closeCancelModal}
                aria-label={t("guardianDash.cancelModal.actions.close")}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="warning-icon">⚠️</div>
              <p className="modal-message">
                {t("guardianDash.cancelModal.message")}
              </p>

              <div className="appointment-summary">
                <div className="summary-row">
                  <span className="label">{t("guardianDash.cancelModal.labels.pet")}:</span>
                  <span className="value">{appointmentToCancel.petName}</span>
                </div>
                <div className="summary-row">
                  <span className="label">{t("guardianDash.cancelModal.labels.clinic")}:</span>
                  <span className="value">{appointmentToCancel.clinicName}</span>
                </div>
                <div className="summary-row">
                  <span className="label">{t("guardianDash.cancelModal.labels.dateTime")}:</span>
                  <span className="value">
                    {t("guardianDash.cancelModal.dateTimeValue", {
                      date: appointmentToCancel.date,
                      time: appointmentToCancel.time,
                    })}
                  </span>
                </div>
                <div className="summary-row">
                  <span className="label">{t("guardianDash.cancelModal.labels.veterinarian")}:</span>
                  <span className="value">{appointmentToCancel.vetName}</span>
                </div>
              </div>

              <p className="warning-text">
                {t("guardianDash.cancelModal.warning")}
              </p>
            </div>

            <div className="modal-actions">
              <button className="btn-cancel-modal" onClick={closeCancelModal}>
                {t("guardianDash.cancelModal.actions.keep")}
              </button>
              <button className="btn-confirm-cancel" onClick={confirmCancellation}>
                {t("guardianDash.cancelModal.actions.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clinic Rating Modal */}
      <RatingModal
        isOpen={!!ratingModalAppointment}
        onClose={closeClinicRatingModal}
        onRate={({ rating, comment }) => {
          // Update local state and call existing submit handler
          setRatingValue(rating);
          setRatingComment(comment);
          // We need to return the promise from handleSubmitClinicRating
          return handleSubmitClinicRating(rating, comment);
        }}
        onSkip={() => handleSkipClinicRating(ratingModalAppointment)}
        title={ratingModalAppointment ? t("guardianDash.appointments.rating.modalTitle", {
          clinicName: ratingModalAppointment.clinicName,
        }) : ""}
        subtitle={t("guardianDash.appointments.rating.modalSubtitle", "How was your visit? Your feedback helps other guardians and the clinic.")}
        initialRating={0}
      />

      {/* Reschedule Appointment Modal */}
      {showRescheduleModal && appointmentToReschedule && (
        <div className="modal-overlay">
          <div className="reschedule-modal">
            <div className="modal-header">
              <h3>{t("guardianDash.rescheduleModal.title")}</h3>
              <button
                className="close-btn"
                onClick={closeRescheduleModal}
                aria-label={t("guardianDash.rescheduleModal.actions.close")}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="current-appointment">
                <h4>{t("guardianDash.rescheduleModal.current.title")}</h4>
                <div className="appointment-summary">
                  <div className="summary-row">
                    <span className="label">{t("guardianDash.rescheduleModal.labels.pet")}:</span>
                    <span className="value">{appointmentToReschedule.petName}</span>
                  </div>
                  <div className="summary-row">
                    <span className="label">{t("guardianDash.rescheduleModal.labels.clinic")}:</span>
                    <span className="value">{appointmentToReschedule.clinicName}</span>
                  </div>
                  <div className="summary-row">
                    <span className="label">{t("guardianDash.rescheduleModal.labels.veterinarian")}:</span>
                    <span className="value">{appointmentToReschedule.vetName}</span>
                  </div>
                  <div className="summary-row">
                    <span className="label">{t("guardianDash.rescheduleModal.labels.currentDateTime")}:</span>
                    <span className="value">
                      {t("guardianDash.rescheduleModal.dateTimeValue", {
                        date: appointmentToReschedule.date,
                        time: appointmentToReschedule.time,
                      })}
                    </span>
                  </div>
                </div>
              </div>

              <div className="new-appointment">
                <h4>{t("guardianDash.rescheduleModal.new.title")}</h4>
                <div className="datetime-inputs">
                  <div className="input-group">
                    <label htmlFor="reschedule-picker">{t("guardianDash.rescheduleModal.labels.date")}:</label>
                    <input
                      ref={reschedulePickerRef}
                      type="text"
                      id="reschedule-picker"
                      placeholder={t("guardianDash.rescheduleModal.placeholders.date")}
                      className="flatpickr-input"
                      readOnly
                    />
                  </div>
                  <div className="input-group">
                    <label htmlFor="time-select">{t("guardianDash.rescheduleModal.labels.time")}:</label>
                    {loadingTimeSlots ? (
                      <div className="loading-time">{t("guardianDash.rescheduleModal.loadingTimes")}</div>
                    ) : selectedDate ? (
                      <select
                        id="time-select"
                        value={selectedTime}
                        onChange={(e) => setSelectedTime(e.target.value)}
                        className="time-select"
                      >
                        <option value="">{t("guardianDash.rescheduleModal.placeholders.time")}</option>
                        {availableTimeSlots
                          .filter(slot => slot.available)
                          .map((slot, index) => (
                            <option key={index} value={slot.value}>
                              {slot.label}
                            </option>
                          ))}
                      </select>
                    ) : (
                      <div className="no-date-selected">{t("guardianDash.rescheduleModal.noDateSelected")}</div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn-cancel-modal" onClick={closeRescheduleModal}>
                {t("guardianDash.rescheduleModal.actions.cancel")}
              </button>
              <button className="btn-confirm-reschedule" onClick={showReschedulePreviewModal}>
                {t("guardianDash.rescheduleModal.actions.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule Preview Modal */}
      {showReschedulePreview && appointmentToReschedule && (
        <div className="modal-overlay">
          <div className="modal reschedule-preview-modal">
            <div className="modal-header">
              <h3>{t("guardianDash.reschedulePreview.title")}</h3>
              <button
                className="close-btn"
                onClick={cancelReschedule}
                aria-label={t("guardianDash.reschedulePreview.actions.close")}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="reschedule-summary">
                <div className="pet-info-header">
                  <div className="pet-avatar">
                    {resolvePetImage(appointmentToReschedule.pet) ? (
                      <img
                        src={resolvePetImage(appointmentToReschedule.pet)}
                        alt={appointmentToReschedule.petName}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className="pet-avatar-fallback" style={{
                      display: resolvePetImage(appointmentToReschedule.pet) ? 'none' : 'flex'
                    }}>
                      {appointmentToReschedule.pet?.species === 'Dog' ? '🐕' : appointmentToReschedule.pet?.species === 'Cat' ? '🐱' : '🐾'}
                    </div>
                  </div>
                  <div className="pet-details">
                    <h3>{appointmentToReschedule.petName}</h3>
                    <p>{appointmentToReschedule.type}</p>
                  </div>
                </div>

                <div className="clinic-info">
                  <h4>{appointmentToReschedule.clinicName}</h4>
                  <p>
                    {t("guardianDash.reschedulePreview.doctorLabel", {
                      name: appointmentToReschedule.vetName,
                    })}
                  </p>
                </div>

                <div className="time-comparison">
                  <div className="time-change-item">
                    <div className="time-label">{t("guardianDash.reschedulePreview.labels.currentDateTime")}:</div>
                    <div className="current-time">
                      <span className="date">{new Date(appointmentToReschedule.date).toLocaleDateString(locale, {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}</span>
                      <span className="time">{appointmentToReschedule.time}</span>
                    </div>
                  </div>

                  <div className="change-arrow">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M13 7L18 12L13 17M6 12H18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>

                  <div className="time-change-item">
                    <div className="time-label">{t("guardianDash.reschedulePreview.labels.newDateTime")}:</div>
                    <div className="new-time">
                      <span className="date">{new Date(selectedDate).toLocaleDateString(locale, {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}</span>
                      <span className="time">{(() => {
                        const timeObj = new Date(`2000-01-01T${selectedTime}:00`);
                        return timeObj.toLocaleTimeString(locale, {
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true
                        });
                      })()}</span>
                    </div>
                  </div>
                </div>

                <div className="reschedule-note">
                  <div className="note-icon">ℹ️</div>
                  <p>{t("guardianDash.reschedulePreview.note")}</p>
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-cancel-modal" onClick={goBackToReschedule}>
                {t("guardianDash.reschedulePreview.actions.back")}
              </button>
              <button className="btn-confirm-reschedule" onClick={finalizeReschedule}>
                {t("guardianDash.reschedulePreview.actions.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SOAP Details Modal for Past Visits */}
      {selectedVisit && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px'
          }}
          onClick={() => setSelectedVisit(null)}
        >
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '900px',
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }} onClick={e => e.stopPropagation()}>
            <div style={{
              padding: '16px 24px',
              borderBottom: '1px solid #E5E7EB',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: '#F9FAFB'
            }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>
                {t('receptionInpatients.details.tabs.soapNotes', { defaultValue: 'SOAP Notes' })} - {(() => {
                  const formatDate = (dateString) => {
                    if (!dateString) return '---';
                    try {
                      const date = new Date(dateString);
                      if (isNaN(date.getTime())) return dateString;
                      return date.toLocaleDateString(locale, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      });
                    } catch {
                      return dateString;
                    }
                  };
                  return formatDate(selectedVisit.visit_date || selectedVisit.created_at);
                })()}
              </h3>
              <button
                onClick={() => setSelectedVisit(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6B7280',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#FEE2E2';
                  e.target.style.color = '#DC2626';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#6B7280';
                }}
              >
                &times;
              </button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
              <SOAPDetailsContent
                isVisible={true}
                inpatientId={String(selectedVisit.type || '').toLowerCase() === 'inpatient' ? (selectedVisit.id || selectedVisit.visit_id || selectedVisit.inpatient_visit_id || selectedVisit.inpatient_id || selectedVisit.pet_visit_id) : null}
                outpatientId={(String(selectedVisit.type || '').toLowerCase() === 'outpatient' || !selectedVisit.type) ? (selectedVisit.id || selectedVisit.visit_id || selectedVisit.outpatient_visit_id || selectedVisit.outpatient_id || selectedVisit.pet_visit_id) : null}
                readOnly={true}
                hideHeader={true}
                petId={selectedPet?.id}
                services={selectedVisit.services || selectedVisit.visit_details || selectedVisit.visitDetails || []}
                planServicesMode="fulfilledOnly"
                visitInfo={{
                  visitDate: selectedVisit.visit_date || selectedVisit.created_at,
                  initialDiagnosis: selectedVisit.reason || '',
                  finalDiagnosis: selectedVisit.final_diag || selectedVisit.final_diagnosis || '',
                  doctorNotes: selectedVisit.doctor_notes || '',
                  prescription: selectedVisit.prescription || ''
                }}
                onClose={() => setSelectedVisit(null)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuardianDash;
